import React, { useState, useEffect, useRef } from 'react';
import PaginationLink from '../components/shared/PaginationLink';
import {
  buildPageHref,
  buildSearchHref,
  getPageFromQuery,
  getSearchFromQuery,
  hasMoreAlgoliaPages,
  stripListingParams,
} from '../util/PaginationUtil';
import {
  sendClickedObjectIDsAfterSearch,
  sendNoResultsEvent,
} from '../util/AlgoliaInsights';
import ResourceFilter from '../components/resources/resource-filter';
import {
  assemble,
  correctUrlFormatForRouting,
  correctDomainForLanguage,
  fetchData,
  fetchDataApiV2,
  tempRender,
  isLinkAbsolute,
  marketoOptions as options,
  usageOptions as usageOptions,
  createFilterData,
  getAlgoliaDatasetValue,
  getAlgoliaIndex,
  getCardImageSrc,
  removeQueryParam,
  isItUndefined,
  noTruncationForAllScreen,
  pushSearchSuggestionClickEvent,
} from '../util/DataUtil';
import ArticleThumbnailCard from '../components/shared/ArticleThumbnailCard';
import { getLanguage, useWindowSize } from '../util/PageUtil';
import { useRouter } from 'next/router';
import ResourceTitle from '../components/resources/resource-title';
import { Constants } from '../util/Constants';
import Image from 'next/image';
import { SearchBarPlacement } from '../constants/SearchBarPlacement';

export function GlobalSearchPage({ data, width, globalMetrics, lang }) {
  const router = useRouter();
  const windowSize = useWindowSize();
  // Server-rendered first slice, so a shared or reloaded search URL paints in
  // one pass and works without JavaScript.
  const serverResults = data?.initialGlobalSearch;
  const serverHits = Array.isArray(serverResults?.hits)
    ? serverResults.hits
    : [];

  const [query, setQuery] = useState(
    router.query.search ? router.query.search : ''
  );
  // Seeded from the URL so a reload of `?page=2` stays on page two.
  const [page, setPage] = useState(() => getPageFromQuery(router?.query));
  const [facetFilters, setFacetFilters] = useState([]);
  const [languageFilter, setLanguageFilter] = useState([]);
  // Seeded too: the results block is gated on this, so without it the
  // server-rendered hits — and the link to the next page — would never make it
  // into the markup.
  const [results, setResults] = useState(serverHits.length > 0);
  const [applyFilter, setApplyFilter] = useState(false);
  const [algoliaData, setAlgoliaData] = useState(serverResults || undefined);
  const [filterProps, setFilterProps] = useState([]);
  const [showMore, setShowMore] = useState(serverHits);
  const [topSearchItems, setTopSearchItems] = useState([]);
  const [hostname, setHostname] = useState('');
  const [queryID, setQueryID] = useState(null);
  const noResultsTrackedRef = useRef(null);
  const cardId = router.query.cardId;

  // The slice the URL asked for is already rendered; refetching it on mount
  // would only replace it with itself.
  // Server-rendered results are always unfiltered, so when the URL carries a
  // filter or a search term the client has to fetch again — skipping that left
  // the filter showing as selected while the list underneath stayed unfiltered.
  const urlNarrowsResults =
    Object.keys(stripListingParams(router?.query) || {}).length > 0 ||
    Boolean(getSearchFromQuery(router?.query));

  const skipInitialFetch = useRef(serverHits.length > 0 && !urlNarrowsResults);

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    setAlgoliaData(fetchAlgoliaData());
    setQuery(router.query.search ? router.query.search : '');
  }, [router.query.search]);

  let hitsPerPage = 16;
  let lng = lang;
  let facetFiltersArray = [];

  useEffect(() => {
    getMostPopularSearchTerms();
  }, []);

  useEffect(() => {
    if (!lng) {
      return;
    }

    fetchAlgoliaData();
  }, [applyFilter, query]);

  const skipInitialPagination = useRef(true);

  useEffect(() => {
    if (!lng) {
      return;
    }

    // On mount the effect that loads results has already fetched the page the
    // URL asked for. This one appends, so letting its first run through would
    // show that page's results twice.
    if (skipInitialPagination.current) {
      skipInitialPagination.current = false;
      return;
    }

    if (page > 0) {
      fetchAlgoliaPagination();
    }
  }, [page]);

  // Hydrating the filters out of the URL is a one-time thing. This effect
  // re-runs whenever its dependencies settle — the author list arriving, for
  // instance — and on a later run it would read the URL again and re-apply it
  // on top of whatever the visitor had since selected, overwriting their choice
  // with a fresh mapping. That looked like the filter being applied and then
  // quietly ignored.
  const hydratedFromUrl = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      let localStorageFilter = localStorage.getItem('name');

      if (!hydratedFromUrl.current && localStorageFilter) {
        // triggerFilter([{name: localStorageFilter}]);
        hydratedFromUrl.current = true;
        triggerFilterFromRedirect([{ name: localStorageFilter }]);
      }

      localStorage.removeItem('name');
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHostname(window.location.hostname);
    }
  }, []);

  useEffect(() => {
    if (!algoliaData || algoliaData.nbHits !== 0 || !queryID) return;
    if (noResultsTrackedRef.current === queryID) return;
    noResultsTrackedRef.current = queryID;
    sendNoResultsEvent(queryID, 'global' + getAlgoliaIndex());
  }, [algoliaData, queryID]);

  let lngArray = [];

  const fetchAlgoliaData = () => {
    // setResults((query.length || facetFilters.length || languageFilter.length) !== 0);

    // let url = createFilterData(lngArray, facetFiltersArray, facetFilters, query, hitsPerPage, page, lng, getAlgoliaDatasetValue(0));
    // let url = createFilterData(lngArray, facetFiltersArray, facetFilters, query, hitsPerPage, page, lng, 'globalprod');
    let url = createFilterData(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      lng,
      'global' + getAlgoliaIndex()
    );

    const fetchAlgolia = async () => {
      await fetch(url, options)
        .then((response) => response.json())
        .then((newResponse) => {
          setShowMore(newResponse.hits);
          setAlgoliaData(newResponse);
          setQueryID(newResponse.queryID ?? null);
          setResults(true);
        });
    };

    fetchAlgolia();
  };

  const fetchAlgoliaPagination = () => {
    // let url = createFilterData(lngArray, facetFiltersArray, facetFilters, query, hitsPerPage, page, lng, 'globalprod');
    let url = createFilterData(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      lng,
      'global' + getAlgoliaIndex()
    );

    const fetchAlgolia = async () => {
      await fetch(url, options)
        .then((response) => response.json())
        .then((newResponse) => {
          setShowMore((previousResponse) => [
            ...previousResponse,
            ...newResponse.hits,
          ]);
          setAlgoliaData(newResponse);
          setQueryID(newResponse.queryID ?? null);
        });
    };

    fetchAlgolia();
  };

  const fetchAlgoliaMostPopular = (queryParam) => {
    let lang = JSON.stringify('search_api_language:' + lng);
    // let url = 'https://SWULV5ONQ5-dsn.algolia.net/1/indexes/drupal?query=' + queryParam + '&facetFilters=[' + lang + ']&hitsPerPage=3&page=0';
    // let url = 'https://O2VJB2KMPB-dsn.algolia.net/1/indexes/drupalprod?query=' + queryParam + '&facetFilters=[' + lang + ']&hitsPerPage=3&page=0';
    let url =
      Constants.algoliaProduction +
      'drupalprod?query=' +
      queryParam +
      '&facetFilters=[' +
      lang +
      ',["status:true"]]&hitsPerPage=3&page=0';

    const fetchData = async () => {
      await fetch(url, options)
        .then((response) => response.json())
        .then((newResponse) => {
          setTopSearchItems((previousResponse) => [
            ...previousResponse,
            ...newResponse.hits,
          ]);
        });
    };
    fetchData();
  };

  const handleSearch = (search) => {
    setQuery(search.length !== 0 ? [search] : []);
    setFilterProps([]);
    setFacetFilters([]);
    setPage(0);
    setApplyFilter(false);
    // Put the term in the address bar so a reload or a shared link restores the
    // same results and pagination composes with it. `buildSearchHref` drops the
    // page: a new search starts at the beginning.
    // Only when the term actually changes what is in the URL.
    //
    // This is handed to the filter component as `onSearchChange`, so it fires
    // while a filter is being applied — with an empty term and a `router.asPath`
    // that has not caught up with the filter's own push yet. Writing then
    // rewound the address to its pre-filter state and wiped the filter out.
    if (getSearchFromQuery(router.query) !== search) {
      router.replace(buildSearchHref(router.asPath, search), undefined, {
        shallow: true,
        scroll: false,
      });
    }
  };

  // `resetPage` is false when the filters are being hydrated from the URL
  // rather than chosen by the visitor. Someone opening a shared
  // `?type=blueprints&search=te&page=2` has not changed anything — treating
  // that as a filter change reset the page to one and stripped it from the
  // address before they saw it.
  const triggerFilter = (filters, resetPage = true) => {
    setFilterProps(filters);

    let searchFilters = [];
    for (let filter of filters) {
      searchFilters.push(filter?.field_source_translation);
    }

    setFacetFilters(searchFilters);
    // Back to the first page: the filtered set is a different, shorter list, so
    // staying on page three would ask for a slice of it that does not exist and
    // the results would come back empty.
    //
    // Skipped when `resetPage` is false — that is the URL being hydrated on
    // arrival, where the page in the address has to survive.
    if (resetPage) {
      setPage(0);
    }

    // The address itself is deliberately not touched here. The filter control
    // writes the URL and drops the page while doing it, and `router.asPath` is
    // still the pre-filter value at this point — rewriting from it rewound the
    // address and threw the filter straight back out.
    setApplyFilter(false);
  };

  const handleClick = (event, urlAlias) => {
    event.stopPropagation();

    if (urlAlias?.url?.length !== 0) {
      if (
        isLinkAbsolute(
          correctDomainForLanguage(urlAlias?.language, hostname) + urlAlias?.url
        )
      ) {
        window.open(
          correctDomainForLanguage(urlAlias?.language, hostname) +
            urlAlias?.url,
          '_blank'
        );
      } else {
        router.push(
          correctDomainForLanguage(urlAlias?.language, hostname) + urlAlias?.url
        );
      }
    }
  };

  const triggerFilterFromRedirect = (filter) => {
    for (let item of data?.categories) {
      if (item.name.toUpperCase() === filter[0].name.toUpperCase()) {
        triggerFilter([item], false);
      }
    }
    setApplyFilter(true);
    // setQuery([]);
  };

  const handleChipClick = (event, filter) => {
    event.stopPropagation();
    for (let item of data?.categories) {
      if (item.name === filter[0].name) {
        triggerFilter([item]);
      }
    }
    setApplyFilter(true);
    setQuery([]);
  };

  const renderCategories = (categories) => {
    if (typeof categories == 'object') {
      return categories.map((item, index) => {
        return (
          <div key={`category-${item || 'category'}`} onClick={(event) => handleChipClick(event, [{ name: item }])}>
            {item}
          </div>
        );
      });
    } else {
      return categories;
    }
  };

  const clearAll = () => {
    // Only when a term is actually in the URL. This is handed to the filter
    // component as a prop and fires while a filter is being applied — rewriting
    // the address unconditionally raced with the filter's own push and wiped
    // the filter straight back out of it.
    if (getSearchFromQuery(router.query)) {
      router.replace(buildSearchHref(router.asPath, ''), undefined, {
        shallow: true,
        scroll: false,
      });
    }
    setFilterProps([]);
    triggerFilter([]);
    setLanguageFilter([]);
    setQuery([]);
    removeQueryParam(router);

    // fetchAlgoliaData()
  };

  const getMostPopularSearchTerms = () => {
    // fetch('https://analytics.algolia.com/2/searches?index=drupal', options)
    fetch(
      'https://analytics.algolia.com/2/searches?index=drupalprod',
      usageOptions
    )
      .then((response) => response.json())
      .then((actualData) => {
        getTopSearchesFromAlgolia(actualData);
      });
  };

  const getTopSearchesFromAlgolia = (searchTerms) => {
    if (!searchTerms?.searches) {
      return;
    }

    for (let item of searchTerms.searches) {
      if (item.nbHits !== 0) {
        fetchAlgoliaMostPopular(item?.search);
      }
    }
  };

  const handleScroll = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Card title (header) — same logic the card previously used inline.
  const getCardTitle = (item) =>
    item?.field_display_title
      ? item?.field_display_title
      : item?.field_template_title
        ? noTruncationForAllScreen(item?.field_template_title, 42, 55, 55, 30)
        : noTruncationForAllScreen(item?.title, 42, 60, 60, 30);

  // Routed href string for the shared card. correctUrlFormatForRouting returns
  // an object { url, language }, which is NOT a valid next/link href (passing it
  // sent every card to the homepage). Resolve it to a plain string the same way
  // the old onClick navigation did: language domain + path.
  const getCardHref = (item) => {
    if (!item?.url) return '/';
    const routed = correctUrlFormatForRouting(item.url);
    if (!routed?.url) return '/';
    return correctDomainForLanguage(routed.language, hostname) + routed.url;
  };

  const getCardTarget = (item) => {
    const href = getCardHref(item);
    return href !== '/' && isLinkAbsolute(href) ? '_blank' : undefined;
  };

  // Subheader meta — same shape as the Featured block: Content Type in the
  // author's position, sitting where Blog shows the author line (bottom-aligned
  // via the shared card). Global search has no date field in Algolia, so only
  // the content type shows; if a date is indexed later, add "| Date" here.
  const renderResourceMeta = (item) => {
    const label = getContentTypeLabel(item);
    if (!label) return null;
    return (
      <div className={'blog-info'}>
        <span className={'info-item'}>{label}</span>
      </div>
    );
  };

  // Content-type meta (subheader) — extracted from the previous inline switch.
  const getContentTypeLabel = (item) => {
    if (item?.content_type) {
      return item.content_type === 'Case Studies'
        ? globalMetrics?.field_case_studies_label?.[0]?.value
        : item.content_type;
    }
    if (item?.type || item?.field_page_type) {
      switch (item?.field_page_type) {
        case 'Content Hub':
          return (
            item?.content_type ||
            globalMetrics?.field_content_hub_label?.[0]?.value ||
            item?.field_page_type
          );
        case 'Content Sub':
          return (
            item?.content_type ||
            globalMetrics?.field_content_sub_label?.[0]?.value ||
            item?.field_page_type
          );
        case 'Partners':
          return (
            item?.content_type ||
            globalMetrics?.field_partners_label?.[0]?.value ||
            item?.field_page_type
          );
        case 'Insights':
          return (
            item?.content_type ||
            globalMetrics?.field_insights_label?.[0]?.value ||
            item?.field_page_type
          );
        case 'Solutions':
        case 'Solutions With Card Stepper':
          return (
            item?.content_type ||
            globalMetrics?.field_solutions_label?.[0]?.value ||
            item?.field_page_type
          );
        default:
          switch (item?.type) {
            case 'location':
              return (
                globalMetrics?.field_facility_label?.[0]?.value || item?.type
              );
            case 'global_data_centre_locations':
              return (
                globalMetrics?.field_global_data_centre_locatio?.[0]?.value ||
                item?.type
              );
            case 'region':
              return (
                item?.content_type ||
                globalMetrics?.field_region_label?.[0]?.value ||
                item?.type
              );
            case 'metro':
              return (
                item?.content_type ||
                globalMetrics?.field_metro_label?.[0]?.value ||
                item?.type
              );
            case 'individual_product_template':
              return (
                item?.content_type ||
                globalMetrics?.field_products_label?.[0]?.value ||
                item?.type
              );
            case 'info_template':
              return (
                globalMetrics?.field_info_template_label?.[0]?.value ||
                item?.type
              );
            case 'press_release':
              return (
                globalMetrics?.field_press_release_label?.[0]?.value ||
                item?.type
              );
            default:
              return '';
          }
      }
    }
    return '';
  };

  // Primary category for the tag chip. Global search only indexes a primary
  // (`primary_source_language`) plus a system/tertiary tag (`system_source_language`,
  // never displayed). Secondary categories are not in Algolia yet — once indexed
  // from Drupal, pass them as `secondaryCategories` to show up to 3 chips.
  const getPrimaryCategory = (item) => {
    const name = item?.name || item?.primary_source_language;
    return name ? { name } : null;
  };

  const renderSearchCards = (data, length = -1) => {
    let result = [];
    if (cardId) {
      length = 1;
    } else {
      length = -1;
    }
    if (!data) {
      return;
    }
    if (length !== -1) {
      data = [...new Set(data)].slice(0, length);
    }

    const locationResults = data.filter(
      (item) => item?.type === 'global_data_centre_locations'
    );
    const regionResults = data.filter((item) => item?.type === 'region');
    const metroResults = data.filter((item) => item?.type === 'metro');
    const facilityResults = data.filter((item) => item?.type === 'location');
    const nonResourceResults = data.filter(
      (item) =>
        item?.type == 'individual_product_template' ||
        item?.type == 'content_template' ||
        item?.type == 'info_template' ||
        item?.type == 'freestyle_template'
    );
    const otherResults = data.filter((item) => {
      return (
        item?.type !== 'global_data_centre_locations' &&
        item?.type !== 'region' &&
        item?.type !== 'metro' &&
        item?.type !== 'facility' &&
        !(
          item?.type === 'individual_product_template' ||
          item?.type === 'content_template' ||
          item?.type === 'info_template' ||
          item?.type === 'freestyle_template'
        )
      );
    });
    const uniqueCombinedData = new Set([
      ...locationResults,
      ...regionResults,
      ...metroResults,
      ...facilityResults,
      ...nonResourceResults,
      ...otherResults,
    ]);
    const combinedDataArray = [...uniqueCombinedData];

    let index = 0;

    for (const [i, item] of combinedDataArray.entries()) {
      result.push(
        <ArticleThumbnailCard
          key={index}
          className="search-card"
          href={getCardHref(item)}
          target={getCardTarget(item)}
          image={getCardImageSrc(item)}
          imageAlt="search-image"
          header={getCardTitle(item)}
          subheader={renderResourceMeta(item)}
          primaryCategory={getPrimaryCategory(item)}
          secondaryCategories={[]
            .concat(item?.secondary_name || [])
            .map((name) => ({ name }))}
          onTagClick={(event, tag) => {
            setQuery(tag?.name || '');
            handleScroll();
          }}
          onClick={() => {
            pushSearchSuggestionClickEvent(
              Array.isArray(query)
                ? (query[0]?.toLowerCase() ?? '')
                : (query?.toLowerCase() ?? ''),
              SearchBarPlacement.GLOBAL_SEARCH_PAGE,
              getCardTitle(item),
              i + 1
            );
            sendClickedObjectIDsAfterSearch(
              queryID,
              item.objectID,
              'global' + getAlgoliaIndex(),
              i + 1
            );
          }}
        />
      );
      index++;
    }

    return result;
  };

  function renderSearchResults(data) {
    if (!data) {
      return;
    }
    if (data.length === 0) {
      return (
        <div className={'no-result'}>
          <h2>
            {globalMetrics?.field_no_results_text?.[0]?.value ||
              'No results were found. Please check the spelling, reset any filters and try again.'}
          </h2>
          <div
            className={'clear-all'}
            onClick={() => {
              clearAll();
            }}
          >
            {globalMetrics?.field_clear_all_label?.[0]?.value ||
              'Clear all search terms.'}
          </div>
          <div className={'most-popular'}>
            <h2>
              {globalMetrics?.field_most_popular_text?.[0]?.value ||
                'Most popular'}
            </h2>
            <div className={'search-results'}>
              {renderSearchCards(topSearchItems, 12)}
            </div>
          </div>
        </div>
      );
    }

    return renderSearchCards(data);
  }

  return (
    <div className={'resource'}>
      <ResourceTitle
        title={data?.field_resources_title[0]?.value}
        subtitle={data?.field_resources_subtitle[0]?.value}
        placeholder={'Search All Resources...'}
      />
      <div className={'global-search'}>
        <ResourceFilter
          metrosLabel={globalMetrics?.field_metro_label?.[0]?.value || 'Metros'}
          search={data?.field_search_resources[0]?.value}
          title={data?.field_filter_resources[0]?.value}
          filterProps={filterProps}
          triggerFilter={triggerFilter}
          data={assemble(data?.categories)}
          onSearchChange={handleSearch}
          handleFilterApply={setApplyFilter}
          searchQuery={query}
          setQuery={setQuery}
          languageFilter={languageFilter}
          setLanguageFilter={setLanguageFilter}
          screenWidth={windowSize.width}
          clearAll={clearAll}
          hideFilter={true}
          clearQuery={clearAll}
          placement={SearchBarPlacement.GLOBAL_SEARCH_PAGE}
        />
      </div>
      {results ? (
        <div className={'search-results-header inner-container'}>
          <h2>{globalMetrics?.field_results_label?.[0]?.value || 'Results'}</h2>
          <div className={'horizontal-line'} />
          <div className={'search-results'}>
            {renderSearchResults(showMore, data)}
          </div>
          {hasMoreAlgoliaPages(algoliaData) && (
              <PaginationLink page={page + 1} onLoadMore={setPage}>
                {globalMetrics?.field_see_more_text?.[0]?.value || 'See More'}
              </PaginationLink>
            )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

export async function categoriesProps(endpoint, lang) {
  return fetchData(endpoint, 'template-categories?lang=' + lang);
}

export async function popularDownloadsProps(endpoint, lang) {
  const res = await fetch(
    endpoint.apiV2Url + 'popular-downloads?langcode=' + lang
  );
  // return fetchData('popular-downloads?lang+' + lang)
  return await res.json();
}

// export async function featuredProps(data) {
//     return fetchData('templates?featured=1&lang=' + data.langcode);
// }

export async function latestProps(endpoint, lang) {
  const res = await fetch(
    endpoint.apiV2Url + 'latest-insights?langcode=' + lang
  );
  return await res.json();
}
